import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { X, Plus, Edit, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

interface DubbingInfo {
  studioAssigned: string;
  assignedDate: string;
  stages: DubbingStage;
  notes?: string;
}

interface WriterInfo {
  agencyType: string;
  name: string;
  assignedDate?: string;
  stages: WritingStage;
}

interface QcInfo {
  type: string;
  qcAgency?: string;
  qcDone: boolean;
  remarksSheetLink?: string;
  owner?: string;
  passedStatus: QcPassedStatus;
}

interface NameChangeInfo {
  changedToLocalizedName?: string;
  status: NameChangeStatus;
}

interface LocalizedVersion {
  language: string;
  status: LocalizedVersionStatus;
  dubbingInfo?: DubbingInfo;
  writerInfo?: WriterInfo;
  qcInfo?: QcInfo;
  dubbedLinkFinal?: string;
  localizationSheetLink?: string;
  nameChangeInfo?: NameChangeInfo;
  missingAdditionalCharacters?: string[];
  localizedEpsDriveLink?: string;
}

interface DubbingProject {
  id?: string;
  showId: string;
  readyForLocalization: boolean;
  showAssignedDate?: string;
  growthPriority: number | null;
  scrapeStatus: boolean;
  srtStatus: boolean;
  localizedVersions: LocalizedVersion[];
}

interface DubbingDetailsData {
  projects: DubbingProject[];
}

interface DubbingDetailsFormProps {
  data: DubbingDetailsData;
  onChange: (data: DubbingDetailsData) => void;
  showId?: string | null;
  onProjectCreate?: (project: Omit<DubbingProject, 'id'>) => Promise<void>;
  onProjectUpdate?: (projectId: string, project: Omit<DubbingProject, 'id'>) => Promise<void>;
  onProjectDelete?: (projectId: string) => Promise<void>;
}

type LocalizedVersionStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'RELEASED';
type DubbingStage = 'SCRIPTING' | 'CASTING' | 'RECORDING' | 'MIXING' | 'FINALIZING' | 'COMPLETED';
type WritingStage = 'NOT_STARTED' | 'FIRST_DRAFT' | 'REVIEW' | 'REWORKING' | 'FINAL_VERSION';
type QcPassedStatus = 'PASSED' | 'FAILED' | 'PENDING';
type NameChangeStatus = 'NOT_APPLICABLE' | 'NOT_STARTED' | 'PARTIALLY_COMPLETED' | 'COMPLETED';

const STATUS_OPTIONS: LocalizedVersionStatus[] = ['NOT_STARTED', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED', 'RELEASED'];
const STAGE_OPTIONS: DubbingStage[] = ['SCRIPTING', 'CASTING', 'RECORDING', 'MIXING', 'FINALIZING', 'COMPLETED'];
const WRITING_STAGE_OPTIONS: WritingStage[] = ['NOT_STARTED', 'FIRST_DRAFT', 'REVIEW', 'REWORKING', 'FINAL_VERSION'];
const QC_PASSED_STATUS_OPTIONS: QcPassedStatus[] = ['PASSED', 'FAILED', 'PENDING'];
const NAME_CHANGE_STATUS_OPTIONS: NameChangeStatus[] = ['NOT_APPLICABLE', 'NOT_STARTED', 'PARTIALLY_COMPLETED', 'COMPLETED'];

export const DubbingDetailsForm: React.FC<DubbingDetailsFormProps> = ({ 
  data, 
  onChange, 
  showId,
  onProjectCreate,
  onProjectUpdate,
  onProjectDelete
}) => {
  // Ensure data has the expected structure with defaults
  const safeData = data || { projects: [] };
  
  const [selectedProject, setSelectedProject] = useState<DubbingProject | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingProject, setEditingProject] = useState<DubbingProject | null>(null);
  const [newLanguage, setNewLanguage] = useState('');
  const [newCharacter, setNewCharacter] = useState('');

  const updateData = (updates: Partial<DubbingDetailsData>) => {
    onChange({ ...safeData, ...updates });
  };

  const addProject = () => {
    if (!showId) return;
    
    const newProject: DubbingProject = {
      // No id for new projects - backend will assign
      showId, // showId comes from parent component
      readyForLocalization: false,
      growthPriority: null,
      scrapeStatus: false,
      srtStatus: false,
      localizedVersions: []
    };
    setEditingProject(newProject);
    setIsEditing(true);
  };

  const editProject = (project: DubbingProject) => {
    setEditingProject({ ...project });
    setIsEditing(true);
  };

  const saveProject = async () => {
    if (!editingProject) return;
    
    try {
      const isExistingProject = editingProject.id && safeData.projects.find(p => p.id === editingProject.id);
      
      // Prepare project data without id
      const { id, ...projectData } = editingProject;
      
      if (isExistingProject) {
        // Update existing project via API if available
        if (onProjectUpdate && editingProject.id) {
          await onProjectUpdate(editingProject.id, projectData);
        }
        // Update local state
        const updatedProjects = safeData.projects.map(p => p.id === editingProject.id ? editingProject : p);
        updateData({ projects: updatedProjects });
      } else {
        // Create new project via API if available
        if (onProjectCreate) {
          await onProjectCreate(projectData);
        }
        // Update local state for new project
        const updatedProjects = [...safeData.projects, editingProject];
        updateData({ projects: updatedProjects });
      }
      
      setSelectedProject(editingProject);
      setIsEditing(false);
      setEditingProject(null);
    } catch (error) {
      console.error('Error saving dubbing project:', error);
      // Keep editing modal open on error
    }
  };

  const deleteProject = async (projectId: string) => {
    try {
      // Delete via API if available
      if (onProjectDelete) {
        await onProjectDelete(projectId);
      }
      // Update local state
      const updatedProjects = safeData.projects.filter(p => p.id !== projectId);
      updateData({ projects: updatedProjects });
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
      }
    } catch (error) {
      console.error('Error deleting dubbing project:', error);
    }
  };

  const addLanguage = () => {
    if (!editingProject || !newLanguage.trim()) return;
    
    const newVersion: LocalizedVersion = {
      language: newLanguage.trim(),
      status: 'NOT_STARTED',
      dubbingInfo: {
        studioAssigned: '',
        assignedDate: new Date().toISOString(),
        stages: 'SCRIPTING',
        notes: ''
      },
      missingAdditionalCharacters: []
    };
    
    setEditingProject({
      ...editingProject,
      localizedVersions: [...editingProject.localizedVersions, newVersion]
    });
    setNewLanguage('');
  };

  const removeLanguage = (index: number) => {
    if (!editingProject) return;
    
    const updatedVersions = editingProject.localizedVersions.filter((_, i) => i !== index);
    setEditingProject({ ...editingProject, localizedVersions: updatedVersions });
  };

  const updateLocalizedVersion = (index: number, field: keyof LocalizedVersion, value: any) => {
    if (!editingProject) return;
    
    const updatedVersions = [...editingProject.localizedVersions];
    updatedVersions[index] = { ...updatedVersions[index], [field]: value };
    setEditingProject({ ...editingProject, localizedVersions: updatedVersions });
  };

  const updateDubbingInfo = (index: number, field: keyof DubbingInfo, value: any) => {
    if (!editingProject) return;
    
    const updatedVersions = [...editingProject.localizedVersions];
    const currentDubbingInfo = updatedVersions[index].dubbingInfo || {
      studioAssigned: '',
      assignedDate: new Date().toISOString(),
      stages: 'SCRIPTING' as DubbingStage,
      notes: ''
    };
    updatedVersions[index] = {
      ...updatedVersions[index],
      dubbingInfo: { ...currentDubbingInfo, [field]: value },
    };
    setEditingProject({ ...editingProject, localizedVersions: updatedVersions });
  };

  const updateNameChangeInfo = (index: number, field: keyof NameChangeInfo, value: any) => {
    if (!editingProject) return;
    
    const updatedVersions = [...editingProject.localizedVersions];
    const currentNameChangeInfo = updatedVersions[index].nameChangeInfo || {
      status: 'NOT_APPLICABLE' as NameChangeStatus,
    };
    updatedVersions[index] = {
      ...updatedVersions[index],
      nameChangeInfo: { ...currentNameChangeInfo, [field]: value },
    };
    setEditingProject({ ...editingProject, localizedVersions: updatedVersions });
  };

  const updateWriterInfo = (index: number, field: keyof WriterInfo, value: any) => {
    if (!editingProject) return;
    
    const updatedVersions = [...editingProject.localizedVersions];
    const currentWriterInfo = updatedVersions[index].writerInfo || {
      agencyType: '',
      name: '',
      stages: 'NOT_STARTED' as WritingStage,
    };
    updatedVersions[index] = {
      ...updatedVersions[index],
      writerInfo: { ...currentWriterInfo, [field]: value },
    };
    setEditingProject({ ...editingProject, localizedVersions: updatedVersions });
  };

  const updateQcInfo = (index: number, field: keyof QcInfo, value: any) => {
    if (!editingProject) return;
    
    const updatedVersions = [...editingProject.localizedVersions];
    const currentQcInfo = updatedVersions[index].qcInfo || {
      type: '',
      qcDone: false,
      passedStatus: 'PENDING' as QcPassedStatus,
    };
    updatedVersions[index] = {
      ...updatedVersions[index],
      qcInfo: { ...currentQcInfo, [field]: value },
    };
    setEditingProject({ ...editingProject, localizedVersions: updatedVersions });
  };

  const addMissingCharacter = (versionIndex: number) => {
    if (!editingProject || !newCharacter.trim()) return;
    
    const updatedVersions = [...editingProject.localizedVersions];
    const currentCharacters = updatedVersions[versionIndex].missingAdditionalCharacters || [];
    updatedVersions[versionIndex] = {
      ...updatedVersions[versionIndex],
      missingAdditionalCharacters: [...currentCharacters, newCharacter.trim()],
    };
    setEditingProject({ ...editingProject, localizedVersions: updatedVersions });
    setNewCharacter('');
  };

  const removeMissingCharacter = (versionIndex: number, charIndex: number) => {
    if (!editingProject) return;
    
    const updatedVersions = [...editingProject.localizedVersions];
    const updatedCharacters = (updatedVersions[versionIndex].missingAdditionalCharacters || []).filter(
      (_, i) => i !== charIndex
    );
    updatedVersions[versionIndex] = {
      ...updatedVersions[versionIndex],
      missingAdditionalCharacters: updatedCharacters,
    };
    setEditingProject({ ...editingProject, localizedVersions: updatedVersions });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Dubbing Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium">Dubbing Projects ({safeData.projects.length})</h3>
              <Button onClick={addProject} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Project
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-6">
              {/* Projects List */}
              <div className="space-y-2">
                <h4 className="font-medium">Projects</h4>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {safeData.projects.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No projects added yet</p>
                  ) : (
                    safeData.projects.map((project, index) => (
                      <div
                        key={project.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedProject?.id === project.id ? 'bg-muted border-primary' : 'hover:bg-muted/50'
                        }`}
                        onClick={() => setSelectedProject(project)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm">Project {index + 1}</p>
                            <p className="text-xs text-muted-foreground">
                              {project.localizedVersions.length} language(s) • Priority: {project.growthPriority || 'None'}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                editProject(project);
                              }}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-destructive"
                              onClick={async (e) => {
                                e.stopPropagation();
                                await deleteProject(project.id!);
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Project Details */}
              <div className="col-span-2">
                {selectedProject ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">Project Details</h4>
                      <Button variant="outline" onClick={() => editProject(selectedProject)}>
                        Edit
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <strong>Ready for Localization:</strong> {selectedProject.readyForLocalization ? 'Yes' : 'No'}
                      </div>
                      <div>
                        <strong>Growth Priority:</strong> {selectedProject.growthPriority || 'Not set'}
                      </div>
                      <div>
                        <strong>Scrape Status:</strong> {selectedProject.scrapeStatus ? 'Yes' : 'No'}
                      </div>
                      <div>
                        <strong>SRT Status:</strong> {selectedProject.srtStatus ? 'Yes' : 'No'}
                      </div>
                    </div>

                    {selectedProject.localizedVersions.length > 0 && (
                      <div className="space-y-3">
                        <h5 className="font-medium">Localized Versions</h5>
                        {selectedProject.localizedVersions.map((version, idx) => (
                          <div key={idx} className="p-3 border rounded">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div><strong>Language:</strong> {version.language}</div>
                              <div><strong>Status:</strong> {version.status.replace('_', ' ')}</div>
                              <div><strong>Studio:</strong> {version.dubbingInfo?.studioAssigned || 'Not assigned'}</div>
                              <div><strong>Stage:</strong> {version.dubbingInfo?.stages || 'Not set'}</div>
                              {version.dubbingInfo?.notes && (
                                <div className="col-span-2"><strong>Notes:</strong> {version.dubbingInfo.notes}</div>
                              )}
                              {version.missingAdditionalCharacters && version.missingAdditionalCharacters.length > 0 && (
                                <div className="col-span-2">
                                  <strong>Missing Characters:</strong>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {version.missingAdditionalCharacters.map((character, charIdx) => (
                                      <Badge key={charIdx} variant="secondary">{character}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-48 text-muted-foreground">
                    Select a project to view details
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Project Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProject?.id && safeData.projects.find(p => p.id === editingProject.id) ? 'Edit Dubbing Project' : 'Add New Dubbing Project'}
            </DialogTitle>
          </DialogHeader>
          {editingProject && (
            <div className="space-y-6">
              {/* Basic Project Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="readyForLocalization"
                    checked={editingProject.readyForLocalization}
                    onCheckedChange={(checked) =>
                      setEditingProject({ ...editingProject, readyForLocalization: checked })
                    }
                  />
                  <Label htmlFor="readyForLocalization">Ready for Localization</Label>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="growthPriority">Growth Priority</Label>
                  <Input
                    id="growthPriority"
                    type="number"
                    placeholder="Enter priority number"
                    value={editingProject.growthPriority || ''}
                    onChange={(e) =>
                      setEditingProject({
                        ...editingProject,
                        growthPriority: e.target.value === '' ? null : parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="scrapeStatus"
                    checked={editingProject.scrapeStatus}
                    onCheckedChange={(checked) =>
                      setEditingProject({ ...editingProject, scrapeStatus: checked })
                    }
                  />
                  <Label htmlFor="scrapeStatus">Scrape Status</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="srtStatus"
                    checked={editingProject.srtStatus}
                    onCheckedChange={(checked) => setEditingProject({ ...editingProject, srtStatus: checked })}
                  />
                  <Label htmlFor="srtStatus">SRT Status</Label>
                </div>
              </div>

              {/* Add Language */}
              <div className="space-y-2">
                <Label>Localized Versions</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., en, es, fr"
                    value={newLanguage}
                    onChange={(e) => setNewLanguage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addLanguage()}
                  />
                  <Button type="button" onClick={addLanguage} disabled={!newLanguage.trim()}>
                    Add Language
                  </Button>
                </div>
              </div>

              {/* Language Versions */}
              {editingProject.localizedVersions.map((version, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium">Language: {version.language}</h4>
                    <Button variant="ghost" size="sm" onClick={() => removeLanguage(index)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <Tabs defaultValue="basic" className="space-y-4">
                    <TabsList>
                      <TabsTrigger value="basic">Basic Info</TabsTrigger>
                      <TabsTrigger value="writer">Writer</TabsTrigger>
                      <TabsTrigger value="qc">Quality Control</TabsTrigger>
                      <TabsTrigger value="links">Links</TabsTrigger>
                    </TabsList>

                    <TabsContent value="basic" className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Status</Label>
                          <Select
                            value={version.status}
                            onValueChange={(value) => updateLocalizedVersion(index, 'status', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((status) => (
                                <SelectItem key={status} value={status}>
                                  {status.replace('_', ' ')}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Dubbing Stage</Label>
                          <Select
                            value={version.dubbingInfo?.stages || 'SCRIPTING'}
                            onValueChange={(value) => updateDubbingInfo(index, 'stages', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STAGE_OPTIONS.map((stage) => (
                                <SelectItem key={stage} value={stage}>
                                  {stage}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Studio Assigned</Label>
                          <Input
                            value={version.dubbingInfo?.studioAssigned || ''}
                            onChange={(e) => updateDubbingInfo(index, 'studioAssigned', e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Assigned Date</Label>
                          <Input
                            type="datetime-local"
                            value={version.dubbingInfo?.assignedDate ? new Date(version.dubbingInfo.assignedDate).toISOString().slice(0, 16) : ''}
                            onChange={(e) =>
                              updateDubbingInfo(index, 'assignedDate', new Date(e.target.value).toISOString())
                            }
                          />
                        </div>

                        <div className="col-span-2 space-y-2">
                          <Label>Notes</Label>
                          <Textarea
                            value={version.dubbingInfo?.notes || ''}
                            onChange={(e) => updateDubbingInfo(index, 'notes', e.target.value)}
                            rows={3}
                          />
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="writer" className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Agency Type</Label>
                          <Input
                            placeholder="e.g., INTERNAL, EXTERNAL"
                            value={version.writerInfo?.agencyType || ''}
                            onChange={(e) => updateWriterInfo(index, 'agencyType', e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Writer Name</Label>
                          <Input
                            value={version.writerInfo?.name || ''}
                            onChange={(e) => updateWriterInfo(index, 'name', e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Writing Stage</Label>
                          <Select
                            value={version.writerInfo?.stages || 'NOT_STARTED'}
                            onValueChange={(value) => updateWriterInfo(index, 'stages', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {WRITING_STAGE_OPTIONS.map((stage) => (
                                <SelectItem key={stage} value={stage}>
                                  {stage}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Writer Assigned Date</Label>
                          <Input
                            type="datetime-local"
                            value={
                              version.writerInfo?.assignedDate
                                ? new Date(version.writerInfo.assignedDate).toISOString().slice(0, 16)
                                : ''
                            }
                            onChange={(e) =>
                              updateWriterInfo(index, 'assignedDate', new Date(e.target.value).toISOString())
                            }
                          />
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="qc" className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>QC Type</Label>
                          <Input
                            placeholder="e.g., INTERNAL, EXTERNAL"
                            value={version.qcInfo?.type || ''}
                            onChange={(e) => updateQcInfo(index, 'type', e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>QC Agency</Label>
                          <Input
                            value={version.qcInfo?.qcAgency || ''}
                            onChange={(e) => updateQcInfo(index, 'qcAgency', e.target.value)}
                          />
                        </div>

                        <div className="flex items-center space-x-2">
                          <Switch
                            id={`qcDone-${index}`}
                            checked={version.qcInfo?.qcDone || false}
                            onCheckedChange={(checked) => updateQcInfo(index, 'qcDone', checked)}
                          />
                          <Label htmlFor={`qcDone-${index}`}>QC Done</Label>
                        </div>

                        <div className="space-y-2">
                          <Label>Passed Status</Label>
                          <Select
                            value={version.qcInfo?.passedStatus || 'PENDING'}
                            onValueChange={(value) => updateQcInfo(index, 'passedStatus', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {QC_PASSED_STATUS_OPTIONS.map((status) => (
                                <SelectItem key={status} value={status}>
                                  {status.replace('_', ' ')}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>QC Owner</Label>
                          <Input
                            value={version.qcInfo?.owner || ''}
                            onChange={(e) => updateQcInfo(index, 'owner', e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Remarks Sheet Link</Label>
                          <Input
                            placeholder="https://..."
                            value={version.qcInfo?.remarksSheetLink || ''}
                            onChange={(e) => updateQcInfo(index, 'remarksSheetLink', e.target.value)}
                          />
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="links" className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Dubbed Link Final</Label>
                          <Input
                            placeholder="https://..."
                            value={version.dubbedLinkFinal || ''}
                            onChange={(e) => updateLocalizedVersion(index, 'dubbedLinkFinal', e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Localization Sheet Link</Label>
                          <Input
                            placeholder="https://..."
                            value={version.localizationSheetLink || ''}
                            onChange={(e) => updateLocalizedVersion(index, 'localizationSheetLink', e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Localized Eps Drive Link</Label>
                          <Input
                            placeholder="https://..."
                            value={version.localizedEpsDriveLink || ''}
                            onChange={(e) => updateLocalizedVersion(index, 'localizedEpsDriveLink', e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Name Change Status</Label>
                          <Select
                            value={version.nameChangeInfo?.status || 'NOT_APPLICABLE'}
                            onValueChange={(value) => updateNameChangeInfo(index, 'status', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {NAME_CHANGE_STATUS_OPTIONS.map((status) => (
                                <SelectItem key={status} value={status}>
                                  {status.replace('_', ' ')}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Changed to Localized Name</Label>
                          <Input
                            value={version.nameChangeInfo?.changedToLocalizedName || ''}
                            onChange={(e) => updateNameChangeInfo(index, 'changedToLocalizedName', e.target.value)}
                          />
                        </div>
                      </div>

                      {/* Missing Characters */}
                      <div className="space-y-2">
                        <Label>Missing Additional Characters</Label>
                        <div className="flex gap-2">
                          <Input
                            placeholder="Add Actor"
                            value={newCharacter}
                            onChange={(e) => setNewCharacter(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && addMissingCharacter(index)}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => addMissingCharacter(index)}
                            disabled={!newCharacter.trim()}
                          >
                            Add
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(version.missingAdditionalCharacters || []).map((character, charIndex) => (
                            <Badge key={charIndex} variant="secondary" className="flex items-center gap-1">
                              {character}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0"
                                onClick={() => removeMissingCharacter(index, charIndex)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              ))}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button onClick={saveProject} disabled={!editingProject}>
                  Save Project
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};